import React from 'react';

import { dataLayerProvider, tracker } from '@femtasy/helpers';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';

import IconButton from 'components/IconButton';
import IconFemtasy from 'components/IconFemtasy';
import Message from 'components/Message';
import NestedLink from 'components/NestedLink';
import PlayButton from 'components/PlayButton';
import Typography from 'components/Typography';

import { StoryActions } from 'graphql/generated/graphql';

import { isProduction } from 'helpers/env';

import { useAuth } from 'hooks/useAuth';
import { usePlayer } from 'hooks/usePlayer';

import { useUrls } from 'constants/urls';

import { AudioCourseStory } from '..';
import { SAC_TRACKING_NAME } from '../../SexualAnxietyClass';
import './style.scss';

export type EpisodeState = 'Highlighted' | 'Seen' | 'Unseen';

export type TrackingType = 'journeys' | 'series';

export interface IProps {
  accordionItemId: string;
  onChange?: (
    event: React.SyntheticEvent<Element, Event>,
    expanded: boolean,
  ) => any;
  onClose?: () => any;
  onOpen?: () => any;
  episodeNumber: number;
  episodeState?: EpisodeState;
  story: AudioCourseStory;
}

const CourseItem = ({
  accordionItemId,
  onChange,
  onClose,
  onOpen,
  episodeNumber,
  episodeState = 'Unseen',
  story,
}: IProps) => {
  const {
    playSeriesAudio,
    togglePlay,
    isLoading,
    isPlaying,
    currentlyPlaying,
  } = usePlayer();

  const { pricingPage } = useUrls();
  const { isAuthenticated } = useAuth();

  const handlePlayButtonRedirect = React.useCallback(() => {
    if (!isAuthenticated) {
      return '/sign-up';
    }

    return pricingPage;
  }, [isAuthenticated, pricingPage]);

  const handleChange = React.useCallback(
    (event: React.SyntheticEvent<Element, Event>, expanded: boolean) => {
      onChange && onChange(event, expanded);

      if (expanded) {
        onOpen && onOpen();
      } else {
        onClose && onClose();
      }
    },
    [onChange, onClose, onOpen],
  );

  const isHighlighted = episodeState === 'Highlighted';
  const isSeen = episodeState === 'Seen';
  const canPlay = story.actions.includes(StoryActions.Play);

  const isAudioPlaying = isPlaying && currentlyPlaying?.id === story.id;
  const isAudioLoading = isLoading && currentlyPlaying?.id === story.id;

  const playStory = React.useCallback(
    (event: React.SyntheticEvent) => {
      event.stopPropagation(); // Prevent accordion from opening

      if (isAudioPlaying) {
        togglePlay();
        tracker.sendEvent(
          SAC_TRACKING_NAME,
          [
            {
              action: 'toggle_playback',
              audioName: story.title,
              episodeNumber,
            },
          ],
          !isProduction(),
          [dataLayerProvider],
        );
      } else {
        playSeriesAudio({ id: story.id });
        tracker.sendEvent(
          SAC_TRACKING_NAME,
          [
            {
              action: 'start_playback',
              audioName: story.title,
              episodeNumber,
            },
          ],
          !isProduction(),
          [dataLayerProvider],
        );
      }
    },
    [
      isAudioPlaying,
      togglePlay,
      story.title,
      story.id,
      episodeNumber,
      playSeriesAudio,
    ],
  );

  return (
    <MuiAccordion
      disableGutters
      elevation={0}
      square
      onChange={handleChange}
      className="CourseAccordionItem"
      data-testid="CourseItem"
    >
      <MuiAccordionSummary
        data-testid={`summary-${accordionItemId}`}
        aria-controls={`${accordionItemId}-content`}
        expandIcon={<IconFemtasy icon="Arrowdown" size="24" />}
        id={accordionItemId}
      >
        {canPlay ? (
          <PlayButton
            size="32"
            isLoading={isAudioLoading}
            isPlaying={isAudioPlaying}
            onClick={playStory}
          />
        ) : (
          <NestedLink to={handlePlayButtonRedirect()}>
            <IconButton icon="Locksolid" size="32" />
          </NestedLink>
        )}

        <div className="summary">
          <Typography
            variant={isHighlighted ? 'h3' : 'body'}
            selectable={false}
          >
            <Message message={story.title} />
          </Typography>
          <div className="summary__info">
            <Typography className="summary__textWithIcon" variant="small-thin">
              <IconFemtasy icon="Clockline" size="16" />
              <Message message={story.durationInMinutes} />
            </Typography>
            {isSeen && (
              <Typography
                className="summary__textWithIcon"
                variant="small-thin"
              >
                <Message
                  message={{
                    id: 'component.SeriesAccordionItem.alreadyPlayed',
                  }}
                />
                <IconFemtasy icon="Checkcirclesolid" size="16" />
              </Typography>
            )}
          </div>
        </div>
      </MuiAccordionSummary>
      <MuiAccordionDetails>
        <Typography variant="body">
          <Message message={story.description} />
        </Typography>
      </MuiAccordionDetails>
    </MuiAccordion>
  );
};

export default CourseItem;
